import { t } from '@trimble/i18n';
import { makeMergedPersisted, useLogger } from '@trimble/solidjs-utils';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { parseEnumValue } from '@trimble/utilities';

import {
  Accessor,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  JSX,
  Setter,
  useContext,
} from 'solid-js';

import { makePersisted } from '@solid-primitives/storage';
import { PERSISTENCE_KEY } from './constants/make-persisted-constants';
import { TcErrorCodes } from '@data-access/connect/tc-tcps';
import {
  SidePanelTabName,
  RightPanelTabName,
  MIN_PANEL_WIDTH_PX,
  restorePersistedRightPanelTab,
} from './constants/panel-constants';
import { asError } from '@trimble/logging';
import { useConnectProject } from '@trimble/connect-project-providers';

export const EXPERIMENTS_KEYS: Record<string, string> = {
  featureExtractionPOC: 'FeatureExtractionPOC',
};
export const EXPERIMENTS: Experiments = {
  [EXPERIMENTS_KEYS['featureExtractionPOC']]: false,
};

export interface AppContextProviderProps {
  children: JSX.Element;
  envName: string;
  deployedTag?: string;
}

export function AppContextProvider(
  props: AppContextProviderProps
): JSX.Element {
  const loggerContext = useLogger();
  const logger = loggerContext.getLogger('AppContextProvider');

  const {
    selectedProjectId,
    setSelectedProjectId,
    selectedProject,
    selectedProjectExplorerUrl,
    selectedRegion,
    fetchProjectDetailsError,
    fetchRegionsError,
  } = useConnectProject();

  const [experiments, setExperiments] = makeMergedPersisted(
    // eslint-disable-next-line solid/reactivity
    createSignal<Experiments>(EXPERIMENTS),
    {
      name: PERSISTENCE_KEY.Experiments,
    }
  );

  // Clean up stale experiment keys that were removed from EXPERIMENTS_KEYS.
  // makeMergedPersisted merges stored values with defaults, so old keys
  // from localStorage survive after removal from the code.
  const knownKeys = new Set<string>(Object.values(EXPERIMENTS_KEYS));
  const currentExperiments = experiments();
  const staleKeys = Object.keys(currentExperiments).filter(
    (key) => !knownKeys.has(key)
  );
  if (staleKeys.length > 0) {
    const cleaned = { ...currentExperiments };
    for (const key of staleKeys) {
      delete cleaned[key];
    }
    setExperiments(cleaned);
  }

  const [selectedSidePanelTab, setSelectedSidePanelTab] = makePersisted(
    // eslint-disable-next-line solid/reactivity
    createSignal<SidePanelTabName | undefined>(undefined),
    {
      name: PERSISTENCE_KEY.SelectedSidePanelTab,
      deserialize: (value) =>
        parseEnumValue(SidePanelTabName, JSON.parse(value)),
    }
  );

  const [sidePanelWidthPx, setSidePanelWidthPx] = makeMergedPersisted(
    // eslint-disable-next-line solid/reactivity
    createSignal(MIN_PANEL_WIDTH_PX),
    {
      name: PERSISTENCE_KEY.SidePanelSize,
    }
  );

  const [selectedRightPanelTab, setSelectedRightPanelTab] = makePersisted(
    // eslint-disable-next-line solid/reactivity
    createSignal<RightPanelTabName | undefined>(undefined),
    {
      name: PERSISTENCE_KEY.RightTabName,
      deserialize: (value: string) =>
        restorePersistedRightPanelTab(
          parseEnumValue(RightPanelTabName, JSON.parse(value))
        ),
    }
  );

  const [rightPanelWidthPx, setRightPanelWidthPx] = makeMergedPersisted(
    // eslint-disable-next-line solid/reactivity
    createSignal(MIN_PANEL_WIDTH_PX),
    {
      name: PERSISTENCE_KEY.RightPanelSize,
    }
  );

  const [imagesLoadingCount, setImagesLoadingCount] = createSignal(0);
  const isImagesLoading = createMemo(() => imagesLoadingCount() > 0);

  const startImagesLoading = (): void => {
    setImagesLoadingCount((count) => count + 1);
  };

  const stopImagesLoading = (): void => {
    setImagesLoadingCount((count) => Math.max(0, count - 1));
  };

  createEffect(() => {
    if (fetchRegionsError()) {
      NotificationEventBus.emit({
        type: 'error',
        message: t('general.unhandledError'),
      });
    }
  });

  createEffect(() => {
    const err = fetchProjectDetailsError();
    if (err) {
      if (isProjectAccessError(err)) {
        const projectId = selectedProjectId();
        const projectUrl = selectedProjectExplorerUrl();
        if (projectId && projectUrl) {
          // Clear local storage of selected project
          setSelectedProjectId(undefined);
          // Redirect to Connect to handle the error case
          window.location.replace(projectUrl);
          return;
        }
      }

      NotificationEventBus.emit({
        type: 'error',
        message: t('general.unhandledError'),
      });
      logger.error(
        'Error loading project',
        { projectId: selectedProjectId(), region: selectedRegion() },
        asError(err)
      );
    }
  });

  function isProjectAccessError(err: unknown): boolean {
    // hey-api throwOnError throws the JSON body, not an Error
    const text = err instanceof Error ? err.message : JSON.stringify(err);
    return (
      text.includes(TcErrorCodes.UserNotInProject) ||
      text.includes(TcErrorCodes.ProjectNotFound) ||
      text.includes(TcErrorCodes.InvalidParam)
    );
  }

  // Set logger project context when the selected project changes.
  createEffect(() => {
    const project = selectedProject();
    loggerContext.setProjectContext(
      project
        ? {
            id: project.id,
            name: project.name,
            location: project.location,
          }
        : undefined
    );
  });

  return (
    <AppContext.Provider
      value={{
        selectedSidePanelTab,
        setSelectedSidePanelTab,
        sidePanelWidthPx,
        setSidePanelWidthPx,
        selectedRightPanelTab,
        setSelectedRightPanelTab,
        rightPanelWidthPx,
        setRightPanelWidthPx,
        experiments,
        setExperiments,
        isImagesLoading,
        startImagesLoading,
        stopImagesLoading,
        get envName() {
          return props.envName;
        },
        get deployedTag() {
          return props.deployedTag;
        },
      }}
    >
      {props.children}
    </AppContext.Provider>
  );
}

export type Experiments = {
  [id: string]: boolean;
};

export interface AppContextType {
  selectedSidePanelTab(): SidePanelTabName | undefined;
  setSelectedSidePanelTab(tabName: SidePanelTabName | undefined): void;
  sidePanelWidthPx(): number;
  setSidePanelWidthPx(width: number): void;

  selectedRightPanelTab(): RightPanelTabName | undefined;
  setSelectedRightPanelTab(tabName: RightPanelTabName | undefined): void;
  rightPanelWidthPx(): number;
  setRightPanelWidthPx(width: number): void;

  experiments: Accessor<Experiments>;
  setExperiments: Setter<Experiments>;

  isImagesLoading: Accessor<boolean>;
  startImagesLoading(): void;
  stopImagesLoading(): void;

  envName: string;
  deployedTag?: string;
}

export const AppContext = createContext<AppContextType>();

export function useAppContext(): AppContextType {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within a AppContextProvider');
  }
  return context;
}
